import { ChangeDetectorRef, Component, DestroyRef, ElementRef, inject, OnInit, signal, ViewChild, AfterViewInit } from '@angular/core';
import { Store } from '@ngxs/store';
import { ExplanationJsonModel, Pages } from '../../explanation.json.model';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { MatSort, MatSortModule, Sort } from '@angular/material/sort';
import { AsyncPipe, DatePipe } from '@angular/common';
import { MatIconButton, MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { getStatusText, StateFilterCBComponent } from '../../../utils/component/state-filter-combobox/state-cb.component';
import { FreeFilterInputComponent } from '../../../utils/component/free-filter-input/free-filter-input.component';
import { LiveAnnouncer } from '@angular/cdk/a11y';
import { EditExplanationComponent } from '../../edit-explanation/edit-explanation/edit-explanation.component';
import { ConfirmDialogComponent } from '../../../core/components/confirm-delete-dialog/confirm-dialog.component';
import { Approval } from '../../../core/types';
import { AppSelectors } from '../../../app.state';
import { PlayerMessageService } from '../../../core/services/player.service';
import { PreviewApiService } from '../../../core/services/preview-api.service';
import { WidgetMessageService } from '../../../core/services/widget-message.service';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { LocalStorageService } from '../../../core/services/local-storage.service';
import { ExplanationEntity } from '../../explanation-entity';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { Observable, combineLatest, switchMap, from, map, tap, BehaviorSubject, firstValueFrom } from 'rxjs';
import { ConsumerEntity } from '../../../consumer/consumer.entity';
import { ConsumerEntityService } from '../../../consumer/services/consumer.entity.service';
import { EntityStateModel, InvalidateLoadedQueries } from '../../../core/state/entity.state';
import { SubjectEntityService } from '../../../subject/services/subject.entity.service';
import { SubjectEntity } from '../../../subject/subject.entity';
import { makeQueryKey } from '../../../utils/entity-states/query-key';
import { ExplanationEntityService } from '../../services/explanation.entity.service';
import { IFilters } from '@datx/jsonapi/interfaces/IFilters';
import { SimpleConfirmData } from '../../../core/components/confirm-dialog-raw/confirm-dialog-raw.component';
import { TableLoadingSpinnerComponent } from '../../../utils/component/table-loading-spinner/table-loading-spinner';
import { HasApprovalState, StateBadgeComponent } from '../../../core/components/state-badge/state-badge.component';
import {
  ContentEntityComponent,
  ContentEntityDialogData
} from '../../../core/components/content-entity/content-entity.component';
import { BaseFilterComponent } from '../../../utils/component/base-filter-component/base-filter-component';

@Component({
  selector: 'pixl-main-explanation',
  imports: [
    MatIcon,
    MatSortModule,
    MatIconButton,
    MatTableModule,
    MatButtonModule,
    MatSelectModule,
    MatTooltipModule,
    MatDialogModule,
    MatListModule,
    MatInputModule,
    DatePipe,
    StateFilterCBComponent,
    FreeFilterInputComponent,
    TranslocoModule,
    TableLoadingSpinnerComponent,
    AsyncPipe,
    MatPaginator,
    StateBadgeComponent,
  ],
  providers: [],
  templateUrl: './explanation.component.html',
  styleUrl: './explanation.component.scss'
})
export class ExplanationComponent extends BaseFilterComponent implements OnInit, AfterViewInit {
  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild('editStateDiv') editStateRef!: ElementRef;

  totalItems = 1000;

  private readonly store = inject(Store);

  public explanationService = inject(ExplanationEntityService);
  private consumerService = inject(ConsumerEntityService);
  private subjectService = inject(SubjectEntityService);
  private widgetService = inject(WidgetMessageService);
  explanations$!: Observable<ExplanationEntity[]>;
  consumers$!: Observable<ConsumerEntity[]>;
  subjects$!: Observable<SubjectEntity[]>;
  deletedIds$ = new BehaviorSubject<string[]>([]);
  public refresh$ = new BehaviorSubject<void>(undefined);
  private readonly pageChange$ = toObservable(this.pageChange);

  public filters: IFilters = {};
  private sortString = 'name';

  private dialog = inject(MatDialog);
  private destroyRef = inject(DestroyRef);
  private cdr = inject(ChangeDetectorRef);
  private _liveAnnouncer = inject(LiveAnnouncer);
  private LIST_FIELDS: string[] | null = ['name', 'approval', 'pages', 'consumerId', 'subjectId', 'consumerData', 'config'];
  private sortFieldName: string | null = null;
  private sortFieldDirection: string | null = null;
  private appState$ = this.store.select(AppSelectors.appState);
  private playerService = inject(PlayerMessageService);
  private previewApiService = inject(PreviewApiService);
  private translocoService = inject(TranslocoService);
  private localStorageService = inject(LocalStorageService);

  public dataSource = new MatTableDataSource<ExplanationJsonModel>([] as ExplanationJsonModel[]);
  public searchFilterHint = signal(false);
  public displayedColumns: string[] = ['edit-actions', 'id', 'name', 'consumer', 'competency', 'pages', 'updated', 'status'];
  public readonly coloredButtonsEnabled = this.localStorageService.coloredButtonsEnabled;
  public readonly explanationStateClass = ExplanationEntity as unknown as new () => HasApprovalState;

  public ngOnInit(): void {
    this.loadRelationData();

    this.explanations$ = combineLatest([
      this.pageChange$,
      this.refresh$,
      this.deletedIds$
    ]).pipe(
      switchMap(([{ pageIndex, pageSize }, , deletedIds]) => {
        const query: Record<string, string | number> = {
          'include': 'consumers,subjects',
          'page[size]': pageSize,
          'page[number]': pageIndex + 1,
          'sort': this.sortString,
        };

        if (this.freeFilter?.trim()?.length >= this.freeFilterLen) {
          query['filter[idOrNameLike]'] = this.freeFilter.trim();
        }

        if (this.statusFilter?.length > 0) {
          query['filter[approval]'] = this.statusFilter.trim();
        }

        if (this.consumerFilter?.length > 0) {
          query['filter[consumers][id]'] = this.consumerFilter.join(',');
        }

        if (this.subjectFilter?.length > 0) {
          query['filter[subjectId]'] = this.subjectFilter.join(',');
        }

        return from(this.explanationService.ensureLoaded(query)).pipe(
          switchMap(() => this.store.select(this.explanationService.allByQuery(query))),
          map((entities: ExplanationEntity[]) => entities.filter(m => !deletedIds.includes(m.id))),
          tap(() => {
            const state = this.store.selectSnapshot(this.explanationService.stateClass) as EntityStateModel<ExplanationEntity>;
            const queryKey = makeQueryKey(query);
            this.totalItems = state?.meta?.[queryKey]?.page?.total ?? 0;
            this.cdr.markForCheck();
          })
        );
      })
    );
  }

  ngAfterViewInit(): void {
    this.paginator().page.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((event: PageEvent) => {
      this.pageChange.set({ pageIndex: event.pageIndex, pageSize: event.pageSize });
    });
  }

  private loadRelationData(): void {
    const consumerQuery = { sort: 'name' };
    this.consumers$ = from(this.consumerService.ensureLoaded(consumerQuery)).pipe(
      switchMap(() => this.store.select(this.consumerService.allByQuery(consumerQuery)))
    );

    const subjectQuery = { sort: 'name' };
    this.subjects$ = from(this.subjectService.ensureLoaded(subjectQuery)).pipe(
      switchMap(() => this.store.select(this.subjectService.allByQuery(subjectQuery)))
    );
  }

  addExplanation(): void {
    const dialogRef = this.dialog.open(EditExplanationComponent, {
      minWidth: '40vw',
      maxWidth: '90vw',
      height: '80vh',
      data: {
        active: true
      }
    });

    dialogRef.afterClosed().subscribe(async (result: Partial<ExplanationEntity> | undefined) => {
      if (result) {
        const newEntityInstance = Object.assign(new ExplanationEntity(), result);
        await this.explanationService.create(newEntityInstance);
        this.store.dispatch(new InvalidateLoadedQueries());
        this.refresh$.next();

        this.widgetService.setMessage({
          message: this.translocoService.translate('explanation_created'),
          messageTye: 'mtSuccess'
        });
      }
    });
  }

  editExplanation(explanation: ExplanationEntity): void {
    this.currentSelectedRow.set(explanation.id);
    const dialogRef = this.dialog.open(EditExplanationComponent, {
      minWidth: '40vw',
      maxWidth: '90vw',
      height: '80vh',
      data: {
        ...explanation,
        consumerExtra: this.getConsumer(explanation.consumerId)
      },
    });

    dialogRef.afterClosed().subscribe(async (updated: Partial<ExplanationEntity> | undefined) => {
      await this.saveUpdatedExplanation(updated);
    });
  }

  public editContentExplanation(explanation: ExplanationEntity): void {
    this.currentSelectedRow.set(explanation.id);
    const dialogRef = this.dialog.open(ContentEntityComponent, {
      minWidth: '98vw',
      height: '98vh',
      data: {
        type: 'explanation',
        model: explanation
      } as ContentEntityDialogData
    });

    dialogRef.afterClosed().subscribe(async (updated: Partial<ExplanationEntity> | undefined) => {
      await this.saveUpdatedExplanation(updated);
    });
  }

  private async saveUpdatedExplanation(updated: Partial<ExplanationEntity> | undefined): Promise<void> {
    if (!updated) return;
    const updatedEntityInstance = Object.assign(new ExplanationEntity(), updated);
    await firstValueFrom(this.explanationService.update(updatedEntityInstance));
    this.refresh$.next();

    this.widgetService.setMessage({
      message: this.translocoService.translate('explanation_saved'),
      messageTye: 'mtSuccess'
    });
  }

  public async copyExplanation(explanation: ExplanationEntity) {
    const copyEntityInstance = Object.assign(new ExplanationEntity(), explanation);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (copyEntityInstance as any).id = undefined;
    copyEntityInstance.name = `${copyEntityInstance.name}${this.translocoService.translate('copy_name_suffix')}`;
    copyEntityInstance.version = 1;
    await this.explanationService.create(copyEntityInstance);
    this.store.dispatch(new InvalidateLoadedQueries());
    this.refresh$.next();

    this.widgetService.setMessage({
      message: this.translocoService.translate('explanation_cloned'),
      messageTye: 'mtSuccess'
    });
  }

  public async deleteExplanation(explanation: ExplanationEntity): Promise<void> {
    if (!explanation?.id) return;
    this.currentSelectedRow.set(explanation.id);

    const confirmed = await firstValueFrom(
      this.dialog.open<ConfirmDialogComponent, SimpleConfirmData, boolean>(ConfirmDialogComponent, {
        data: {
          name: String(explanation.name),
          headerText: this.translocoService.translate('explanation_delete'),
        }
      }).afterClosed()
    );

    if (!confirmed) return;

    await firstValueFrom(this.explanationService.delete(explanation.id));
    this.store.dispatch(new InvalidateLoadedQueries());
    this.refresh$.next();

    this.widgetService.setMessage({
      message: this.translocoService.translate('explanation_deleted'),
      messageTye: 'mtSuccess'
    });
  }

  public getPages(val: Pages[]): number {
    if (val) {
      return val.length;
    }
    return 0;
  }

  public announceSortChange(sortState: Sort) {
    if (sortState.direction) {
      this._liveAnnouncer.announce(`Sorted ${sortState.direction}ending`);
    }
    else {
      this._liveAnnouncer.announce('Sorting cleared');
    }
    this.sortData(sortState);
  }

  public sortData(sort: Sort): void {
    this.sortString = sort.direction === 'desc' ? `-${sort.active}` : sort.active;
    this.refresh$.next();
  }

  public getCreatedDate(id: string): Date {
    return new Date(parseInt(id.substring(0, 8), 16) * 1000);
  }

  public getStateText(approval: Approval): string {
    return this.translocoService.translate(getStatusText(approval));
  }

  public getStatusColor(approval: Approval): string {
    return {
      Dormant: 'badge-dormant',
      Edit: 'badge-edit',
      Pending: 'badge-pending',
      Live: 'badge-live',
    }[approval] || '';
  }

  getConsumer(consumerId: string, getName = false): ConsumerEntity | string {
    if (!consumerId) return '---';
    const state = this.store.selectSnapshot(this.consumerService.stateClass);
    const consumer = this.consumerService.stateClass.byId(state)(consumerId);
    return getName ? (consumer?.name || consumerId) : consumer;
  }

  getSubject(subjectId: string, getName = false): SubjectEntity | string {
    if (!subjectId) return '---';
    const state = this.store.selectSnapshot(this.subjectService.stateClass);
    const subject = this.subjectService.stateClass.byId(state)(subjectId);
    return getName ? (subject?.name || subjectId) : subject;
  }

  public changeExplanationState(explanation: ExplanationEntity) {
    if (explanation.approval === 'Dormant') {
      return;
    }

    if (this.editStateRef.nativeElement.classList.contains('open')) {
      this.editStateRef.nativeElement.classList.remove('open');
    }
    else {
      this.editStateRef.nativeElement.classList.add('open');
    }
  }

  public previewPlayer2(explanation: ExplanationEntity) {
    this.currentSelectedRow.set(explanation.id);
    this.playerService.openPlayerPreview(explanation.consumerId, 'explanation', explanation.id, false);
  }
}
